import { useEffect, useState } from "react"

import { Checkbox, Flex, Modal, Radio, Typography } from "antd"
import { useTranslation } from "react-i18next"

import Icon from "../../Icon"
import ButtonComponent from "../Button"
import InfoTooltip from "../InfoTooltip/InfoTooltip"

import styles from "./ExportDataModal.module.less"

const { Title, Text } = Typography

const ExportDataModal = ({
  open,
  onClose,
  onExport,
  exporting = false,
  periodLabel,
  tables = [],
  formats = []
}) => {
  const { t } = useTranslation()

  const [selectedTables, setSelectedTables] = useState([])
  const [format, setFormat] = useState(null)

  // The Modal keeps this component mounted between opens (only its content is torn down), so
  // the picks have to be cleared explicitly on each open rather than relying on remount.
  useEffect(() => {
    if (!open) return
    setSelectedTables([])
    setFormat(null)
  }, [open])

  const isExportDisabled = !selectedTables.length || !format

  const soloTable =
    selectedTables.length === 1 && tables.find((table) => table.key === selectedTables[0])
  const forbiddenFormats = soloTable?.forbiddenFormatsWhenAlone || []
  const forbiddenFormatsKey = forbiddenFormats.join(",")

  useEffect(() => {
    if (format && forbiddenFormats.includes(format)) setFormat(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forbiddenFormatsKey])

  const handleTableChange = (key, checked) => {
    setSelectedTables((current) =>
      checked ? [...current, key] : current.filter((item) => item !== key)
    )
  }

  const handleExport = () => {
    onExport?.({ tables: selectedTables, format })
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      closeIcon={<Icon type="cross_small" />}
      footer={null}
      width={440}
      classNames={{ content: "clear-bg" }}
      title={
        <Title level={5} className={styles.title}>
          {t("Export Data")}
        </Title>
      }
    >
      <Flex vertical className={styles.description}>
        <Text type="secondary">{t("Choose data tables and format to export text.")}</Text>
        {periodLabel && (
          <Text type="secondary">
            {t("Data export period:")} <Text className={styles.periodValue}>{periodLabel}.</Text>
          </Text>
        )}
      </Flex>

      <Flex vertical gap={24} className={styles.body}>
        <Flex vertical gap={12} className={styles.section}>
          <Text type="secondary" className={styles.sectionLabel}>
            {t("Select data to export")}
          </Text>
          <Flex vertical gap={8}>
            {tables.map((table) => (
              <Flex key={table.key} align="center" className={styles.optionRow}>
                <Checkbox
                  checked={selectedTables.includes(table.key)}
                  onChange={(e) => handleTableChange(table.key, e.target.checked)}
                >
                  {t(table.label)}
                </Checkbox>
                {table.tooltip && <InfoTooltip title={t(table.tooltip)} />}
              </Flex>
            ))}
          </Flex>
        </Flex>

        <Flex vertical gap={12} className={styles.section}>
          <Text type="secondary" className={styles.sectionLabel}>
            {t("Select data format")}
          </Text>
          <Radio.Group value={format} onChange={(e) => setFormat(e.target.value)}>
            <Flex vertical gap={8}>
              {formats.map((option) => (
                <Radio
                  key={option.value}
                  value={option.value}
                  disabled={forbiddenFormats.includes(option.value)}
                >
                  {t(option.label)}
                </Radio>
              ))}
            </Flex>
          </Radio.Group>
        </Flex>
      </Flex>

      <Flex justify="flex-end" gap={12} className={styles.footer}>
        <ButtonComponent size="large" onClick={onClose}>
          {t("Cancel")}
        </ButtonComponent>
        <ButtonComponent
          size="large"
          type="primary"
          disabled={isExportDisabled || exporting}
          loading={exporting}
          onClick={handleExport}
        >
          {t("Export")}
        </ButtonComponent>
      </Flex>
    </Modal>
  )
}

export default ExportDataModal
